'use client'

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toastService } from "../../../services/ToastService";
import { Loader2, Calendar, FileDown, AlertTriangle } from 'lucide-react'
import PaymentsAPI from '../../../api/PaymentsAPI'
import SectionTitle from '../../../components/SectionTitle/SectionTitle'
import { ButtonV2 } from '../../../components/ButtonV2/ButtonV2'

export default function InvoicesTab() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const firstAvailableYear = 2025;
  const firstAvailableMonth = 10; // October

  // Set default to first available period (October 2025)
  const getDefaultYear = () => {
    return Math.max(firstAvailableYear, currentYear);
  };

  const getDefaultMonth = () => {
    const defaultYear = getDefaultYear();
    if (defaultYear === firstAvailableYear) {
      return Math.max(firstAvailableMonth, currentMonth);
    }
    return currentMonth;
  };

  const [selectedYear, setSelectedYear] = useState<number>(getDefaultYear());
  const [selectedMonth, setSelectedMonth] = useState<number>(getDefaultMonth());
  const [reportData, setReportData] = useState<{ totalApplicationFeesCents: number; currency: string } | null>(null);
  const [hasVatConfigured, setHasVatConfigured] = useState<boolean>(true);

  // Only show years from 2025 onwards
  const years = Array.from({ length: currentYear - firstAvailableYear + 1 }, (_, i) => currentYear - i);
  
  const allMonths = [
    { value: 1, label: t("global.january") },
    { value: 2, label: t("global.february") },
    { value: 3, label: t("global.march") },
    { value: 4, label: t("global.april") },
    { value: 5, label: t("global.may") },
    { value: 6, label: t("global.june") },
    { value: 7, label: t("global.july") },
    { value: 8, label: t("global.august") },
    { value: 9, label: t("global.september") },
    { value: 10, label: t("global.october") },
    { value: 11, label: t("global.november") },
    { value: 12, label: t("global.december") },
  ];
  
  // Filter months based on selected year
  const months = (() => {
    if (selectedYear === firstAvailableYear) {
      // For 2025, only show October onwards
      const filtered = allMonths.filter(m => m.value >= firstAvailableMonth);
      // If current year is 2025, also limit to current month
      if (currentYear === firstAvailableYear) {
        return filtered.filter(m => m.value <= currentMonth);
      }
      return filtered;
    } else if (selectedYear === currentYear) {
      // For current year (after 2025), limit to current month
      return allMonths.filter(m => m.value <= currentMonth);
    } else if (selectedYear > currentYear) {
      // For future years, don't show any months (not available yet)
      return [];
    } else {
      // For past years (before 2025 or between 2025 and current), show all months
      return allMonths;
    }
  })();

  const isCurrentSelectedMonth = selectedYear === currentYear && selectedMonth === currentMonth;

  const fetchMonthlyReport = async () => {
    setIsLoading(true);
    try {
      const response = await PaymentsAPI.getMonthlyReporting(selectedYear, selectedMonth);
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching monthly report:', error);
      toastService.showToast(t("settings.payments.invoices.errors.load_failed"), 'error');
      setReportData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const checkVatStatus = async () => {
    try {
      const response = await PaymentsAPI.getVatStatus();
      setHasVatConfigured(response.data.hasVatConfigured);
    } catch (error) {
      console.error('Error checking VAT status:', error);
      setHasVatConfigured(true);
    }
  };

  useEffect(() => {
    if (selectedYear === firstAvailableYear) {
      // For 2025, ensure month is at least October
      if (selectedMonth < firstAvailableMonth) {
        setSelectedMonth(firstAvailableMonth);
        return;
      }
      // If current year is 2025, ensure month doesn't exceed current month
      if (currentYear === firstAvailableYear && selectedMonth > currentMonth) {
        setSelectedMonth(currentMonth);
        return;
      }
    } else if (selectedYear === currentYear && selectedMonth > currentMonth) {
      // For current year, ensure month doesn't exceed current month
      setSelectedMonth(currentMonth);
      return;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  // Fetch data when year or month changes
  useEffect(() => {
    fetchMonthlyReport();
    checkVatStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedMonth]);

  const formatAmount = (cents: number, currency: string = 'EUR'): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
    }).format(cents / 100);
  };

  const handleGenerateInvoice = async () => {
    if (!reportData) {
      await fetchMonthlyReport();
    }

    setIsDownloading(true);
    try {
      await PaymentsAPI.downloadInvoicePDF(selectedYear, selectedMonth);
      toastService.showToast(t("settings.payments.invoices.download_success"), 'success');
    } catch (error) {
      console.error('Error downloading invoice PDF:', error);
      toastService.showToast(t("settings.payments.invoices.errors.download_failed"), 'error');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <label className="text-sm font-medium flex items-center space-x-2">
          <Calendar className="h-4 w-4" />
          <span>{t("settings.payments.invoices.select_period")}</span>
        </label>
        <div className="flex gap-2">
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
            className="flex-1 px-3 py-2 border rounded-md bg-background text-sm"
          >
            {months.map((month) => (
              <option key={month.value} value={month.value}>
                {month.label}
              </option>
            ))}
          </select>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="flex-1 px-3 py-2 border rounded-md bg-background text-sm"
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-10 space-y-4">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
          <p className="text-muted-foreground">{t("settings.payments.invoices.loading")}</p>
        </div>
      ) : reportData ? (
        <div className="space-y-4">
          {!hasVatConfigured && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 flex items-start">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-yellow-900">
                  {t("settings.payments.invoices.vat_warning.title")}
                </h4>
                <p className="text-yellow-700 mt-1 text-sm">
                  {t("settings.payments.invoices.vat_warning.description")}
                </p>
              </div>
            </div>
          )}
          <SectionTitle text={t("settings.payments.invoices.commission_summary")} />
          <div className="rounded-lg border p-6 bg-muted/50">
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-muted-foreground">
                  {t("settings.payments.invoices.platform_commission")}
                </span>
                <span className="text-2xl font-bold">
                  {formatAmount(reportData.totalApplicationFeesCents, reportData.currency)}
                </span>
              </div>
              <div className="flex justify-between items-center text-xs text-muted-foreground">
                <span>
                  {months.find(m => m.value === selectedMonth)?.label} {selectedYear}
                </span>
              </div>
            </div>
            {reportData.totalApplicationFeesCents > 0 && (
              <div className="mt-4">
                <ButtonV2
                  onClick={handleGenerateInvoice}
                  disabled={isLoading || isDownloading || isCurrentSelectedMonth}
                  variant="outline"
                  className="w-full sm:w-auto"
                >
                  {isDownloading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t("settings.payments.invoices.downloading")}
                    </>
                  ) : (
                    <>
                      <FileDown className="mr-2 h-4 w-4" />
                      {t("settings.payments.invoices.download_invoice")}
                    </>
                  )}
                </ButtonV2>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          <p>{t("settings.payments.invoices.no_data")}</p>
        </div>
      )}
    </div>
  );
}
